// Growth › Self-signups (owner, November 4: "a self signup only page where you
// can do everything around self signups ... see what mails are scheduled to go
// out for them"). One page, three tabs, told apart by ?tab=:
//
//   /growth/self-signups                  Signups: everyone who signed up on scorelm.com
//   /growth/self-signups?tab=emails       every email to them, scheduled or sent (?state=sent)
//   /growth/self-signups?tab=automatic    every email they get by itself: the follow-up after
//                                         signup with its settings, wait included (FollowUpCard),
//                                         then what the platform sends
//
// The follow-up had its own tab until the owner asked for its wait "under
// automatic emails" (November 4); ?tab=follow-up still opens Automatic emails.
// The Signups view under Tenants stays too: same signups, same tenant pages.

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import type { SignupEmail, SignupRow } from '@/api/growthSignups'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  FilterChips,
  Pagination,
  SearchInput,
  SkeletonRows,
  Tabs,
  useToast,
  type Column,
} from '@/components/ui'
import { formatDate, formatDateTime } from '@/lib/format'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { EMAIL_STATUS_LABELS, EMAIL_STATUS_TONE, stateLabel } from '@/lib/signups'
import { FollowUpCard } from './FollowUpCard'
import { OutreachBadge } from './signupShared'

const PAGE_SIZE = 25
const TABS = ['signups', 'emails', 'automatic'] as const
type Tab = (typeof TABS)[number]

/** Search params, with page 1 and empty values left out. */
function usePageParams() {
  const [searchParams, setSearchParams] = useSearchParams()
  const update = (patch: Record<string, string | number | undefined>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '' || (k === 'page' && v === 1)) next.delete(k)
          else next.set(k, String(v))
        }
        if (!('page' in patch)) next.delete('page')
        return next
      },
      { replace: true },
    )
  const rawPage = Number(searchParams.get('page'))
  return { searchParams, update, page: Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1 }
}

// ─── Signups ────────────────────────────────────────────────────────────────

const SIGNUP_COLUMNS: Column<SignupRow>[] = [
  {
    id: 'league',
    header: 'League',
    primary: true,
    cell: (r) => (
      <span className="grid min-w-0 gap-0.5">
        <span className="break-words font-semibold">{r.league.name}</span>
        <span className="truncate text-xs font-medium text-muted">{r.league.subdomain}</span>
      </span>
    ),
    className: 'min-w-[12rem]',
  },
  {
    id: 'person',
    header: 'Signed up by',
    cell: (r) => (
      <span className="grid min-w-0 gap-0.5">
        <span className="break-words">{r.person.name ?? r.person.email ?? '—'}</span>
        {r.person.name && r.person.email ? <span className="truncate text-xs text-muted">{r.person.email}</span> : null}
      </span>
    ),
  },
  { id: 'when', header: 'Signed up', cell: (r) => <span className="whitespace-nowrap tabular">{formatDate(r.signed_up_at)}</span> },
  { id: 'source', header: 'Source', cell: (r) => <span className="text-muted">{r.source ?? 'Not recorded'}</span> },
  { id: 'state', header: 'State', cell: (r) => <span className="whitespace-nowrap">{stateLabel(r.lifecycle)}</span> },
  { id: 'emails', header: 'Emails', cell: (r) => <OutreachBadge status={r.outreach.status} /> },
]

function SignupsTab() {
  const { searchParams, update, page } = usePageParams()
  const q = searchParams.get('q') ?? ''
  const params = { q, page, page_size: PAGE_SIZE }
  const { data, error, status, refetch } = useQuery(['signups', 'self-signups', params], (signal) => api.growth.signups.list(params, signal), { keepPrevious: true })
  const rows = data?.signups ?? []
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput label="Search signups" placeholder="Search name, email or league" value={q} onChange={(v) => update({ q: v })} className="sm:max-w-sm" />
        <ButtonLink to="/tenants?view=signups" size="sm" variant="ghost">
          Open in Tenants
        </ButtonLink>
      </div>
      <Card flush className="overflow-hidden">
        {status === 'loading' ? (
          <SkeletonRows rows={5} label="Loading signups" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load signups" onRetry={refetch} />
        ) : rows.length === 0 ? (
          <EmptyState title={q ? 'No signups match' : 'No signups yet'} description={q ? 'Try another search.' : 'Everyone who signs up on scorelm.com shows here.'} />
        ) : (
          <DataTable caption="Self-signups" rows={rows} columns={SIGNUP_COLUMNS} rowKey={(r) => r.id} rowHref={(r) => `/tenants/signups/${r.id}`} />
        )}
      </Card>
      {data && data.total > PAGE_SIZE ? <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onPageChange={(p) => update({ page: p, q: q || undefined })} noun="signups" /> : null}
    </div>
  )
}

// ─── Emails ─────────────────────────────────────────────────────────────────

function RemoveButton({ email }: { email: SignupEmail }) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Remove
      </Button>
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Take this email off the Schedule?"
        description={`"${email.subject}" to ${email.signup.person_name ?? email.email} won't go.`}
        confirmLabel="Remove"
        tone="danger"
        onConfirm={async () => {
          try {
            await api.growth.schedule.remove('one_off', email.id)
            invalidateQueries('signups')
            invalidateQueries('growth')
            toast.success('Removed. It won’t go.')
            setOpen(false)
          } catch (err) {
            toast.error(errorMessage(err))
          }
        }}
      />
    </>
  )
}

function emailColumns(scheduled: boolean): Column<SignupEmail>[] {
  return [
    {
      id: 'to',
      header: 'To',
      primary: true,
      cell: (e) => (
        <span className="grid min-w-0 gap-0.5">
          <Link to={`/tenants/signups/${e.signup.id}`} className="break-words font-semibold text-ink hover:underline">
            {e.signup.person_name ?? e.email}
          </Link>
          <span className="truncate text-xs text-muted">{e.signup.league_name}</span>
        </span>
      ),
      className: 'min-w-[11rem]',
    },
    {
      id: 'email',
      header: 'Email',
      cell: (e) => (
        <span className="grid min-w-0 gap-1">
          <span className="break-words">{e.subject}</span>
          <span className="flex flex-wrap gap-1.5">
            {e.automatic === 'signup_follow_up' ? <Badge tone="neutral">Follow-up after signup</Badge> : <Badge tone="outline">One-off</Badge>}
            <span className="text-xs text-muted">Signed by {e.signer_name}</span>
          </span>
        </span>
      ),
      className: 'min-w-[14rem]',
    },
    {
      id: 'when',
      header: scheduled ? 'Goes' : 'When',
      cell: (e) => <span className="whitespace-nowrap tabular">{formatDateTime(scheduled ? e.scheduled_for : (e.sent_at ?? e.created_at))}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (e) => (
        <span className="grid gap-0.5">
          <Badge tone={EMAIL_STATUS_TONE[e.status]}>{EMAIL_STATUS_LABELS[e.status]}</Badge>
          {e.status === 'dry_run' ? <span className="text-xs text-muted">Sending was off</span> : e.error ? <span className="text-xs text-muted">{e.error.replace(/_/g, ' ')}</span> : null}
        </span>
      ),
    },
    ...(scheduled ? [{ id: 'actions', header: <span className="sr-only">Actions</span>, align: 'right' as const, cell: (e: SignupEmail) => <RemoveButton email={e} /> }] : []),
  ]
}

function EmailsTab() {
  const { searchParams, update, page } = usePageParams()
  const state = searchParams.get('state') === 'sent' ? 'sent' : 'scheduled'
  const params = { state, page, page_size: PAGE_SIZE } as const
  const { data, error, status, refetch } = useQuery(['signups', 'self-signup-emails', params], (signal) => api.growth.signups.emails(params, signal), { keepPrevious: true })
  const rows = data?.emails ?? []
  return (
    <div className="grid gap-4">
      <FilterChips
        label="Which emails"
        options={[
          { value: 'scheduled', label: 'Scheduled', count: data?.counts.scheduled },
          { value: 'sent', label: 'Sent and done', count: data?.counts.sent },
        ]}
        value={state}
        onChange={(v) => update({ state: v === 'sent' ? 'sent' : undefined })}
      />
      <Card flush className="overflow-hidden">
        {status === 'loading' ? (
          <SkeletonRows rows={4} label="Loading emails" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load emails" onRetry={refetch} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={state === 'scheduled' ? 'Nothing scheduled' : 'No emails yet'}
            description={
              state === 'scheduled'
                ? 'When the follow-up is on, each new signup’s email waits here until it goes.'
                : 'Every email to someone who signed up shows here once it goes: the follow-up and the ones you write.'
            }
            action={
              state === 'scheduled' ? (
                <ButtonLink to="?tab=automatic" size="sm">
                  Follow-up settings
                </ButtonLink>
              ) : undefined
            }
          />
        ) : (
          <DataTable caption={state === 'scheduled' ? 'Scheduled emails to signups' : 'Emails sent to signups'} rows={rows} columns={emailColumns(state === 'scheduled')} rowKey={(e) => e.id} />
        )}
      </Card>
      {state === 'sent' ? <p className="text-xs text-muted">Each one that really went is also in Gmail, in contact@scorelm.com’s Sent Mail.</p> : null}
      {data && data.total > PAGE_SIZE ? <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onPageChange={(p) => update({ page: p, state: state === 'sent' ? 'sent' : undefined })} noun="emails" /> : null}
    </div>
  )
}

// ─── Automatic emails ───────────────────────────────────────────────────────

interface AutoEmail {
  when: string
  what: string
  subject: string
  note?: string
}

/** What the platform sends by itself (backend email-templates, trial-notices.ts, signup-cleanup.ts). Their words are in the code. */
const PLATFORM_EMAILS: AutoEmail[] = [
  { when: 'As they start signing up', what: 'Verification code', subject: '123456 is your ScoreLM verification code' },
  { when: 'As they finish', what: 'Welcome', subject: '{League} is live on ScoreLM', note: 'With links to their site and admin.' },
  { when: '2 days before the 7-day trial ends', what: 'Trial ending', subject: 'Your ScoreLM trial for {League} ends in 2 days' },
  { when: 'When the trial ends', what: 'Trial ended', subject: 'Your ScoreLM trial for {League} has ended' },
  {
    when: '7 days before a never-paid league is deleted',
    what: 'Deletion warning',
    subject: '{League} will be deleted on ScoreLM soon',
    note: 'Only when the never-paid cleanup is turned on on the server. It’s off: nothing is deleted or sent.',
  },
]

function AutomaticTab() {
  return (
    <div className="grid gap-4">
      <FollowUpCard />
      <Card flush className="overflow-hidden">
        <div className="px-4 pt-4">
          <CardHeader title="Sent by the platform" description="To the person who signed up, from the product itself. Their words are in the code, not in Templates." />
        </div>
        <ul className="divide-y divide-line">
          {PLATFORM_EMAILS.map((e) => (
            <li key={e.what} className="grid gap-1 px-4 py-3 sm:grid-cols-[12rem_minmax(0,1fr)] sm:gap-4">
              <span className="text-xs font-semibold text-muted">{e.when}</span>
              <span className="grid min-w-0 gap-0.5">
                <span className="text-[13px] font-semibold text-ink">{e.what}</span>
                <span className="break-words text-[13px] text-muted">“{e.subject}”</span>
                {e.note ? <span className="text-xs text-subtle">{e.note}</span> : null}
              </span>
            </li>
          ))}
        </ul>
        <p className="border-t border-line px-4 py-3 text-xs text-muted">ScoreLM also gets a “New self-serve signup” email for each one.</p>
      </Card>
    </div>
  )
}

// ─── The page ───────────────────────────────────────────────────────────────

const TAB_LABELS: Record<Tab, string> = { signups: 'Signups', emails: 'Emails', automatic: 'Automatic emails' }

export default function SelfSignupsPage() {
  useDocumentTitle('Self-signups')
  const [searchParams] = useSearchParams()
  const raw = searchParams.get('tab') === 'follow-up' ? 'automatic' : searchParams.get('tab')
  const tab: Tab = (TABS as readonly string[]).includes(raw ?? '') ? (raw as Tab) : 'signups'
  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader title="Self-signups" description="Everyone who signs up on scorelm.com, and every email they get from us." />
      <Tabs
        label="Self-signups sections"
        items={TABS.map((t) => ({ to: t === 'signups' ? '/growth/self-signups' : `/growth/self-signups?tab=${t}`, label: TAB_LABELS[t], active: t === tab }))}
      />
      {tab === 'signups' ? <SignupsTab /> : tab === 'emails' ? <EmailsTab /> : <AutomaticTab />}
    </div>
  )
}
